import type { Role } from '@attendance/shared';
import type { FeatureFlags, FeatureKey } from '@attendance/shared/features';
import { useBrandingStore } from '../../stores/brandingStore.js';
import type { NavLeaf } from './navItems.js';

/* ===========================================================================
 * WHO SEES A NAV ROW, AND WHETHER A ROW IS THE PAGE YOU ARE ON.
 *
 * Lifted out of `navItems.ts`, which is the LIST. These are the predicates
 * applied to it: they name no row, and every one takes what it needs as an
 * argument. `NavLeaf` comes back the other way as a TYPE-ONLY import, which
 * is erased at compile time — so the two files do not close a runtime cycle,
 * and the note in `navItems.ts` about not reaching back for one string still
 * holds.
 *
 * `navItems.ts` re-exports all of these, so the call sites that import them
 * from there keep working.
 * ======================================================================== */

/** Role gate for a single nav item. */
export function canSee(item: { roles?: Role[] }, role: Role | undefined): boolean {
  return !item.roles || (!!role && item.roles.includes(role));
}

/** Feature gate for a single nav item. Untagged items are always on; a tagged
 * item shows only when its flag is not explicitly off (fail-open on absent). */
export function featureOn(
  item: { feature?: FeatureKey | FeatureKey[] },
  features: FeatureFlags | undefined
): boolean {
  if (!item.feature || !features) return true;
  const keys = Array.isArray(item.feature) ? item.feature : [item.feature];
  return keys.every((k) => features[k] !== false);
}

/**
 * The wording to render for an item, given the tenant's flags — `labelWithout`
 * when its named feature is explicitly off, the item's own label otherwise.
 * Fail-open on an absent flag set, exactly like `featureOn`.
 */
export function navLabelOf(
  item: NavLeaf,
  features: FeatureFlags | undefined
): { labelKey: string; label: string } {
  const alt = item.labelWithout;
  if (alt && features && features[alt.feature] === false) {
    return { labelKey: alt.labelKey, label: alt.label };
  }
  return { labelKey: item.labelKey, label: item.label };
}

/**
 * True when the tenant is running the construction terminology profile
 * (login-less site workers, Sites & Camps). Read straight from the branding
 * store so the existing `isVisible`/`allDestinations` call sites (Sidebar,
 * CommandPalette) pick up the profile gate without a signature change. The
 * Sidebar subscribes to `branding`, so it re-renders — and re-runs this
 * filter — whenever branding resolves or changes.
 */
export function isConstructionProfile(): boolean {
  return useBrandingStore.getState().branding?.terminology?.profile === 'construction';
}

/** Profile gate for a single nav item. Items not tagged are always allowed. */
export function profileOn(item: { hideInConstruction?: boolean }): boolean {
  return !item.hideInConstruction || !isConstructionProfile();
}

/** Combined role + feature + profile visibility for a single nav item. */
export function isVisible(
  item: NavLeaf,
  role: Role | undefined,
  features: FeatureFlags | undefined
): boolean {
  return canSee(item, role) && featureOn(item, features) && profileOn(item);
}

/** Active for `/` only on exact match; otherwise on the path or a sub-path. */
export function pathMatches(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}
